import React, { useState, useEffect, useCallback } from 'react';
import '../styles/Reports.css';
import { SearchSelect } from './FieldComponents';
import { Minus, PlusIcon, X, Plus, GripVertical, ChevronDown, Loader2 } from 'lucide-react';
import { DndProvider, useDrag, useDrop } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import Modal from './Modal';

const ItemTypes = {
    FIELD: 'field',
    COLUMN: 'column',
};

const DraggableField = ({ field }) => {
    const displayName = (() => {
        const actualFieldId = (typeof field?.id === 'object' && field.id !== null && field.id.id) ? field.id.id : field?.id;
        const actualFieldName = (typeof field?.id === 'object' && field.id !== null && field.id.name) ? field.id.name : field?.name;
        if (actualFieldId === 'contacts.full_name' || actualFieldName === 'contacts.full_name') {
            return 'Contact';
        }
        return actualFieldName || String(actualFieldId ?? '');
    })();

    const [{ isDragging }, drag] = useDrag(() => ({
        type: ItemTypes.FIELD,
        item: { field },
        collect: (monitor) => ({
            isDragging: !!monitor.isDragging(),
        }),
    }));

    return (
        <span
            ref={drag}
            className={`report-field ${isDragging ? 'dragging' : ''}`}
            style={{ opacity: isDragging ? 0.5 : 1, cursor: 'move' }}
        >
                {displayName}
        </span>
    );
};

const ColumnItem = ({ field, onRemove, index, moveColumn, customFields, onEditBlankColumn, onUpdateContactProperty }) => {
    const [isEditing, setIsEditing] = useState(false);
    const [editName, setEditName] = useState(field?.name || '');

    // Contact property options
    const contactPropertyOptions = [
        { value: 'full_name', label: 'Contact' },
        { value: 'first_name', label: 'First Name' },
        { value: 'middle_name', label: 'Middle Name' },
        { value: 'last_name', label: 'Last Name' },
        { value: 'company_name', label: 'Company Name' },
        { value: 'nickname', label: 'Nickname' },
        { value: 'prefix', label: 'Prefix' },
        { value: 'suffix', label: 'Suffix' },
        { value: 'salutation', label: 'Salutation' },
        { value: 'job_title', label: 'Job Title' },
        { value: 'department', label: 'Department' },
        { value: 'date_of_birth', label: 'Date of Birth' },
        { value: 'date_of_death', label: 'Date of Death' },
        { value: 'is_deceased', label: 'Deceased' },
        { value: 'is_company', label: 'Company' },
        { value: 'created_at', label: 'Created At' },
        { value: 'updated_at', label: 'Updated At' },
        { value: 'contact_type', label: 'Contact Type' },
        { value: 'gender', label: 'Gender' },
        { value: 'phone_number', label: 'Phone Number' },
        { value: 'email', label: 'Email' },
        { value: 'address', label: 'Address' },
    ];

    // Dropdown field options (for built-in dropdown fields like case_type_id, phase_id, etc.)
    const dropdownFieldOptions = [
        { value: 'label', label: 'Label (Text)' },
        { value: 'id', label: 'ID (Number)' },
    ];

    // Check if this field is a contact field (field_id === 8)
    const isContactField = (() => {
        if (field?.field_id === 8) return true;
        
        // Extract actual field ID (handle nested structure)
        const actualFieldId = (typeof field?.id === 'object' && field.id !== null && field.id.id) ? field.id.id : field?.id;
        
        // Also check in customFields
        if (customFields && Array.isArray(customFields)) {
            const foundField = customFields.find(f => String(f.id) === String(actualFieldId));
            if (foundField && foundField.field_id === 8) return true;
        }
        
        // Check if it's a built-in contact field (leads.referral_contact, leads.db_origination, contacts.full_name, etc.)
        const contactFieldIds = [
            'leads.referral_contact',
            'leads.db_origination',
            'contacts.full_name',
            'contacts.first_name',
            'contacts.last_name',
            'contacts.company_name'
        ];
        
        return contactFieldIds.includes(String(actualFieldId));
    })();

    // Check if this is a dropdown field (field_id === 10) but NOT a user field
    const isDropdownField = (() => {
        // Custom fields with field_id === 10
        if (field?.field_id === 10) return true;
        
        // Extract actual field ID (handle nested structure)
        const actualFieldId = (typeof field?.id === 'object' && field.id !== null && field.id.id) ? field.id.id : field?.id;
        
        // Also check in customFields
        if (customFields && Array.isArray(customFields)) {
            const foundField = customFields.find(f => String(f.id) === String(actualFieldId));
            if (foundField && foundField.field_id === 10) return true;
        }
        
        // Built-in dropdown fields (case_type_id, phase_id, subphase_id, template_id, marketing_source, marketing_lead)
        const dropdownFieldIds = [
            'leads.case_type_id',
            'cases.phase_id',
            'cases.subphase_id',
            'cases.template_id',
            'leads.marketing_source',
            'leads.marketing_lead'
        ];
        
        return dropdownFieldIds.includes(String(actualFieldId));
    })();

    const [{ isDragging }, drag] = useDrag(() => ({
        type: ItemTypes.COLUMN,
        item: { index, type: 'column' },
        collect: (monitor) => ({
            isDragging: !!monitor.isDragging(),
        }),
    }), [index]);

    const [, drop] = useDrop(() => ({
        accept: ItemTypes.COLUMN,
        hover: (item, monitor) => {
            if (item.index === index) return;
            
            const dragIndex = item.index;
            const hoverIndex = index;
            
            if (dragIndex === hoverIndex) {
                return;
            }
            
            moveColumn(dragIndex, hoverIndex);
            item.index = hoverIndex;
        },
    }), [index, moveColumn]);

    // Safety check - if field is null/undefined, return null AFTER hooks
    if (!field) {
        console.error('ColumnItem received null field at index', index);
        return null;
    }

    // Handle field name lookup - ALWAYS use field's own name for contact fields
    const fieldName = (() => {
        // Extract actual ID if it's wrapped in an object
        const actualFieldId = (typeof field.id === 'object' && field.id !== null && field.id.id) ? field.id.id : field.id;
        
        // Also extract name if it's nested in field.id
        const actualFieldName = (typeof field.id === 'object' && field.id !== null && field.id.name) ? field.id.name : field.name;
        
        // For contact fields (contacts.*), ALWAYS use the field's own name, not the lookup
        // This prevents all contact fields from showing as "Client"
        if (actualFieldId === 'contacts.full_name' || actualFieldName === 'contacts.full_name') {
            return 'Contact';
        }

        if (typeof actualFieldId === 'string' && actualFieldId.startsWith('contacts.')) {
            if (actualFieldName) return actualFieldName;
        }
        
        // For non-contact fields, check customFields first
        if (customFields && Array.isArray(customFields)) {
            const foundField = customFields.find(f => String(f.id) === String(actualFieldId));
            if (foundField) return foundField.name;
        }
        
        // Fall back to the field's own name property
        if (actualFieldName) return actualFieldName;
        
        // Last resort: use the actual ID as string
        return String(actualFieldId);
    })();

    const handleEditSave = () => {
        if (editName.trim() && onEditBlankColumn) {
            onEditBlankColumn(index, editName.trim());
        }
        setIsEditing(false);
    };

    const handleEditCancel = () => {
        setEditName(field.name || '');
        setIsEditing(false);
    };

    return (
        <div 
            ref={(node) => drag(drop(node))}
            className={`column-item ${isDragging ? 'dragging' : ''}`}
            style={{ opacity: isDragging ? 0.5 : 1, padding: (isContactField || isDropdownField) ? '4px 36px 4px 12px' : '10px 36px 10px 12px' }}
        >
            <div className="column-drag-handle">
                <GripVertical size={16} className="column-grip" />
            </div>
            {isEditing ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: '5px', flex: 1 }}>
                    <input
                        type="text"
                        value={editName}
                        onChange={(e) => setEditName(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') handleEditSave();
                            if (e.key === 'Escape') handleEditCancel();
                        }}
                        onBlur={handleEditSave}
                        autoFocus
                        style={{ flex: 1, fontSize: '12px', padding: '2px 4px' }}
                    />
                </div>
            ) : (
                <>
                    <span 
                        className='column-name' 
                        style={{ cursor: field.isBlank ? 'pointer' : 'default' }}
                        onClick={() => {
                            if (field.isBlank) {
                                setIsEditing(true);
                                setEditName(field.name || '');
                            }
                        }}
                        title={field.isBlank ? 'Click to edit blank column name' : ''}
                    >
                        {fieldName}{(Number(field.add_item) === 1 || field.computed_type === "total_section") && ` (${field.section_name})`}
                        {field.isBlank && <span style={{ opacity: 0.6, marginLeft: '4px' }}>(blank)</span>}
                        {isContactField && (
                            <span style={{ opacity: 0.7, marginLeft: '4px', fontSize: '11px' }}>
                                ({contactPropertyOptions.find(opt => opt.value === field.contactProperty)?.label || field.contactProperty || 'Full Name'})
                            </span>
                        )}
                        {isDropdownField && (
                            <span style={{ opacity: 0.7, marginLeft: '4px', fontSize: '11px' }}>
                                ({dropdownFieldOptions.find(opt => opt.value === field.dropdownReturn)?.label || field.dropdownReturn || 'Label'})
                            </span>
                        )}
                    </span>
                    {isContactField && (
                        <div className='filter-group'>
                            <div className='select-wrapper'>
                                <select 
                                    className='filter-select'
                                    value={field.contactProperty || 'full_name'}
                                    onChange={(e) => {
                                        if (onUpdateContactProperty) {
                                            onUpdateContactProperty(index, e.target.value);
                                        }
                                    }}
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    {contactPropertyOptions.map(option => (
                                        <option key={option.value} value={option.value}>
                                            {option.label}
                                        </option>
                                    ))}
                                </select>
                                <ChevronDown size={16} className='select-chevron'/>
                            </div>
                        </div>
                    )}
                    {isDropdownField && (
                        <div className='filter-group'>
                            <div className='select-wrapper'>
                                <select 
                                    className='filter-select'
                                    value={field.dropdownReturn || 'label'}
                                    onChange={(e) => {
                                        if (onUpdateContactProperty) {
                                            onUpdateContactProperty(index, e.target.value, 'dropdownReturn');
                                        }
                                    }}
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    {dropdownFieldOptions.map(option => (
                                        <option key={option.value} value={option.value}>
                                            {option.label}
                                        </option>
                                    ))}
                                </select>
                                <ChevronDown size={16} className='select-chevron'/>
                            </div>
                        </div>
                    )}
                    {onRemove && <X size={16} onClick={() => onRemove(index)} className='report-section-open x' />}
                </>
            )}
        </div>
    );
};

const ColumnsList = ({ selectedFields, onRemove, moveColumn, customFields, onAddField, onEditBlankColumn, onUpdateContactProperty, loadingCustomFields }) => {
    const [{ isOver }, drop] = useDrop(() => ({
        accept: [ItemTypes.FIELD, ItemTypes.COLUMN],
        drop: (item, monitor) => {
            if (item.field && onAddField) {
                onAddField(item.field);
            }
        },
        collect: (monitor) => ({
            isOver: !!monitor.isOver(),
        }),
    }), [onAddField]);

    return (
        <div 
            className={`columns-container ${isOver ? 'drop-active' : ''}`} 
            ref={drop}
        >
            <h3 className="columns-title">Selected Columns</h3>
            
            {loadingCustomFields ? (
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '20px',
                    gap: '8px',
                    minHeight: '100px'
                }}>
                    <Loader2 className='spinner' size={16} />
                    <span style={{ fontSize: '13px', color: '#666' }}>Loading available fields...</span>
                </div>
            ) : selectedFields.length === 0 ? (
                <div className="empty-columns-message">
                    Drag fields here to add columns to your report
                </div>
            ) : (
                <div className="columns-list">
                    {selectedFields.filter(Boolean).map((field, index) => (
                        <ColumnItem
                            key={`column-${field?._uid || index}`}
                            field={field}
                            index={index}
                            onRemove={onRemove}
                            moveColumn={moveColumn}
                            customFields={customFields}
                            onEditBlankColumn={onEditBlankColumn}
                            onUpdateContactProperty={onUpdateContactProperty}
                        />
                    ))}
                </div>
            )}
        </div>
    );
};

export const ReportColumns = ({ selectedReport, selectedFields, setSelectedFields, customFields, sections, loadingCustomFields }) => {
    const [selectedSelection, setSelectedSection] = useState(null);
    const [searchCriteria, setSearchCriteria] = useState(null);
    const [criteriaExpanded, setCriteriaExpanded] = useState(false);
    const [showBlankColumnModal, setShowBlankColumnModal] = useState(false);
    const [blankColumnName, setBlankColumnName] = useState('');
    const banishedFields = [13, 14, 15, 16, 18, 19, 21, 23];

    const generateFieldUid = () => `col_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;

    useEffect(() => {
        if (!Array.isArray(selectedFields) || selectedFields.length === 0) return;

        let changed = false;
        const normalized = selectedFields.map(field => {
            if (!field || field._uid) return field;
            changed = true;
            return { ...field, _uid: generateFieldUid() };
        });

        if (changed) {
            setSelectedFields(normalized);
        }
    }, [selectedFields, setSelectedFields]);

    const handleFieldDrop = useCallback((field) => {
        // Always allow adding, even if field already exists (for multiple contact property columns)
        
        // Check if field.id already has contacts.{property} pattern
        const contactsMatch = String(field.id).match(/^contacts\.([a-zA-Z_]+)$/);
        const isContactField = field.field_id === 8 || 
                               contactsMatch ||
                               ['leads.referral_contact', 'leads.db_origination', 'leads.intake_by', 
                                'leads.assigned_to', 'leads.assigned_to_attorney', 'leads.referred_to'].includes(String(field.id));
        
        // Check if it's a dropdown field
        const isDropdownField = field.field_id === 10 ||
                                ['leads.case_type_id', 'cases.phase_id', 'cases.subphase_id', 
                                 'cases.template_id', 'leads.marketing_source', 'leads.marketing_lead'].includes(String(field.id));
        
        // If field already has contacts.{property} format, use that property
        // Otherwise default to 'full_name' for contact fields
        let contactProperty = undefined;
        if (isContactField) {
            if (contactsMatch) {
                contactProperty = contactsMatch[1]; // Use the property from the ID
            } else {
                contactProperty = field.contactProperty || 'full_name';
            }
        }
        
        const dropdownReturn = isDropdownField ? (field.dropdownReturn || 'label') : undefined;
        
        // IMPORTANT: Keep the original field ID (leads.referral_contact, etc.) 
        // and store the contactProperty separately for backend processing
        let fieldId = field.id;
        let fieldName = field.name;
        if (isContactField && contactProperty) {
            // Generate appropriate name for contact field
            const propertyLabels = {
                'full_name': 'Contact',
                'first_name': 'First Name',
                'middle_name': 'Middle Name', 
                'last_name': 'Last Name',
                'company_name': 'Company Name',
                'nickname': 'Nickname',
                'prefix': 'Prefix',
                'suffix': 'Suffix',
                'salutation': 'Salutation',
                'job_title': 'Job Title',
                'department': 'Department',
                'date_of_birth': 'Date of Birth',
                'date_of_death': 'Date of Death',
                'is_deceased': 'Deceased',
                'is_company': 'Company',
                'created_at': 'Created At',
                'updated_at': 'Updated At',
                'contact_type': 'Contact Type',
                'gender': 'Gender',
                'phone_number': 'Phone Number',
                'email': 'Email',
                'address': 'Address'
            };
            
            // Preserve the original field name if it's descriptive (not generic "Client")
            if (field.name && field.name !== 'Client' && !field.name.includes('Full Name')) {
                fieldName = field.name;
                // If not using full_name property, append the property label
                if (contactProperty !== 'full_name') {
                    fieldName = `${field.name} - ${propertyLabels[contactProperty] || contactProperty}`;
                }
            } else {
                // Generic contact field - just use the property label
                fieldName = propertyLabels[contactProperty] || contactProperty;
            }
        }
        
        const newField = { 
            ...field,
            id: fieldId,  // Keep original ID like "leads.referral_contact"
            name: fieldName,
            contactProperty,
            dropdownReturn
        };
        setSelectedFields(prev => [...prev, newField]);
    }, []);

    const updateContactProperty = (index, property, propertyType = 'contactProperty') => {
        setSelectedFields(prev => {
            const newFields = [...prev];
            const field = newFields[index];
            
            // Update the property
            newFields[index] = { ...field, [propertyType]: property };
            
            // Update field name for contact fields (but keep the original field ID!)
            if (propertyType === 'contactProperty') {
                // DO NOT change the field ID - keep original like "leads.referral_contact"
                // The contactProperty is sent separately to the backend
                
                // Generate descriptive name based on the property
                const propertyLabels = {
                    'full_name': 'Contact',
                    'first_name': 'First Name',
                    'middle_name': 'Middle Name', 
                    'last_name': 'Last Name',
                    'company_name': 'Company Name',
                    'nickname': 'Nickname',
                    'prefix': 'Prefix',
                    'suffix': 'Suffix',
                    'salutation': 'Salutation',
                    'job_title': 'Job Title',
                    'department': 'Department',
                    'date_of_birth': 'Date of Birth',
                    'date_of_death': 'Date of Death',
                    'is_deceased': 'Deceased',
                    'is_company': 'Company',
                    'created_at': 'Created At',
                    'updated_at': 'Updated At',
                    'contact_type': 'Contact Type',
                    'gender': 'Gender',
                    'phone_number': 'Phone Number',
                    'email': 'Email',
                    'address': 'Address'
                };
                
                // Determine base name from original field
                let baseName = '';
                const originalName = field.name || '';
                
                // Check if this is a special contact field (referral, db_origination, etc.)
                if (field.id && typeof field.id === 'string') {
                    if (field.id.includes('referral_contact')) {
                        baseName = 'Referral Contact';
                    } else if (field.id.includes('db_origination')) {
                        baseName = 'DB Origination';
                    } else if (field.id.includes('intake_by')) {
                        baseName = 'Intake By';
                    } else if (field.id.includes('assigned_to_attorney')) {
                        baseName = 'Assigned Attorney';
                    } else if (field.id.includes('assigned_to')) {
                        baseName = 'Assigned To';
                    } else if (field.id.includes('referred_to')) {
                        baseName = 'Referred To';
                    } else if (originalName && originalName !== 'Client' && !originalName.includes('Full Name')) {
                        baseName = originalName;
                    }
                }
                
                // Construct the display name
                const propertyLabel = propertyLabels[property] || property;
                if (baseName && property !== 'full_name') {
                    newFields[index].name = `${baseName} - ${propertyLabel}`;
                } else if (baseName) {
                    newFields[index].name = baseName;
                } else {
                    newFields[index].name = propertyLabel;
                }
            }
            
            return newFields;
        });
    };

    const addBlankColumn = () => {
        if (blankColumnName.trim()) {
            const encodedName = encodeURIComponent(blankColumnName.trim());
            const blankField = {
                id: `blank_${Date.now()}_${encodedName}`, // Encode name in ID
                name: blankColumnName.trim(),
                isBlank: true,
                field_id: null,
                section_id: '[-1]'
            };
            setSelectedFields(prev => [...prev, blankField]);
            setBlankColumnName('');
            setShowBlankColumnModal(false);
        }
    };

    const editBlankColumn = (index, newName) => {
        setSelectedFields(prev => {
            const newFields = [...prev];
            if (newFields[index] && newFields[index].isBlank) {
                const encodedName = encodeURIComponent(newName);
                const timestamp = newFields[index].id.split('_')[1] || Date.now();
                newFields[index] = { 
                    ...newFields[index], 
                    name: newName,
                    id: `blank_${timestamp}_${encodedName}` // Update ID with new name
                };
            }
            return newFields;
        });
    };

    const removeColumn = (index) => {
        setSelectedFields(prev => {
            const newFields = [...prev];
            newFields.splice(index, 1);
            return newFields.filter(Boolean); // Filter out any null/undefined values
        });
    };

    const moveColumn = useCallback((fromIndex, toIndex) => {
        setSelectedFields(prev => {
            const newFields = [...prev];
            const [movedItem] = newFields.splice(fromIndex, 1);
            newFields.splice(toIndex, 0, movedItem);
            return newFields;
        });
    }, []);

    return (
        <DndProvider backend={HTML5Backend}>
            <div className='report-container'>
                <div className='criteria-picker'>
                    {loadingCustomFields ? (
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            padding: '20px',
                            gap: '8px',
                            minHeight: '100px'
                        }}>
                            <Loader2 className='spinner' size={16} />
                            <span style={{ fontSize: '13px', color: '#666' }}>Loading available fields...</span>
                        </div>
                    ) : (
                        <>
                            <div style={{ display: 'flex', gap: 10, width: '100%', justifyContent: 'flex-start', alignItems: 'center' }}>
                                <SearchSelect
                                    value={searchCriteria}
                                    options={customFields && Array.isArray(customFields) ? Object.fromEntries(customFields.map(field => {
                                        const fieldTypeLabels = {
                                            1: 'Text - Single Line',
                                            2: 'Text - Multi Line',
                                            3: 'Amount - Currency',
                                            4: 'Amount - Percent',
                                            5: 'Amount - Number',
                                            6: 'Date',
                                            7: 'Time',
                                            8: 'Person',
                                            9: 'Person List',
                                            10: 'Dropdown List',
                                            11: 'Multi-Select List',
                                            12: 'Yes/No',
                                            13: 'Subheader',
                                            14: 'Instructions',
                                            15: 'File',
                                            16: 'Multi File',
                                            17: 'Calculation',
                                            18: 'DocGen',
                                            19: 'Add Item',
                                            20: 'Deadline',
                                            21: 'Reference',
                                            22: 'Search Select',
                                            23: 'Action',
                                            24: 'ReportFusion',
                                            25: 'Rating'
                                        };
                                        const typeLabel = field.field_id ? fieldTypeLabels[field.field_id] : null;
                                        const displayName = typeLabel ? `${field.name} (${typeLabel})` : field.name;
                                        return [field.id, displayName];
                                    })) : {}}
                                    placeholder='Search fields...'
                                    onChange={(selected) => {
                                        setSearchCriteria(selected);
                                        if (customFields && Array.isArray(customFields)) {
                                            const field = customFields.find(f => f.id == selected);
                                            if (field) handleFieldDrop(field);
                                        }
                                    }}
                                />
                                <button 
                                    className='action quaternary' 
                                    onClick={() => setShowBlankColumnModal(true)}
                                >
                                    <Plus size={16} />
                                    Blank Column
                                </button>
                            </div>
                            <div className='sections'>
                        {sections
                            .filter(section => {
                                if (searchCriteria) {
                                    const selectedField = customFields.find(f => f.id === searchCriteria || (f.field_id && Number(f.field_id) === Number(searchCriteria)));
                                    if (!selectedField) return false;

                                    const sectionIds = Array.isArray(selectedField.section_id)
                                        ? selectedField.section_id
                                        : JSON.parse(selectedField.section_id || '[]');

                                    return sectionIds.includes(Number(section.id));
                                } else {
                                    return customFields.some(field => {
                                        const sectionIds = Array.isArray(field.section_id)
                                            ? field.section_id
                                            : JSON.parse(field.section_id || '[]');
                                        return sectionIds.includes(Number(section.id));
                                    });
                                }
                            })
                            .sort((a, b) => {
                                const aOrder = Number(a.order_id);
                                const bOrder = Number(b.order_id);
                                
                                // If both negative, reverse order (larger negative first: -1, -2, -3)
                                if (aOrder < 0 && bOrder < 0) {
                                    return bOrder - aOrder;
                                }
                                // If one negative and one positive, negative comes first
                                if (aOrder < 0) return -1;
                                if (bOrder < 0) return 1;
                                // Both positive, normal order
                                return aOrder - bOrder;
                            })
                            .slice(0, sections.length)
                            .map(section => (
                                <div
                                    key={section.id}
                                    className={`section-item ${selectedSelection === section.id ? 'active-section' : ''}`}
                                    onClick={() => setSelectedSection(selectedSelection === section.id ? null : section.id)}
                                >
                                    <span className='report-section-name'>
                                        {section.name}
                                        {selectedSelection !== section.id ? (
                                            <PlusIcon size={16} className='report-section-open' />
                                        ) : (
                                            <Minus size={16} className='report-section-open' />
                                        )}
                                    </span>
                                    {selectedSelection === section.id && (
                                    <div className='report-fields'>
                                        {(customFields && Array.isArray(customFields) ? customFields : [])
                                            .filter(field => {
                                                const sectionIds = Array.isArray(field.section_id)
                                                    ? field.section_id
                                                    : JSON.parse(field.section_id || '[]');

                                                const isInSection = sectionIds.includes(Number(section.id));
                                                
                                                // Only apply banished fields logic to numeric field_ids (custom fields)
                                                // Contact fields (like contacts.id, contacts.full_name) should not be filtered by banishedFields
                                                const isNotBanished = field.field_id ? !banishedFields.includes(Number(field.field_id)) : true;

                                                if (searchCriteria) {
                                                    // For search criteria, handle both numeric and string field IDs
                                                    const searchMatch = field.field_id ? 
                                                        Number(field.id) === Number(searchCriteria) || field.field_id === Number(searchCriteria) :
                                                        field.id === searchCriteria;
                                                    return isInSection && isNotBanished && searchMatch;
                                                }

                                                return isInSection && isNotBanished;
                                            })
                                            .map(field => (
                                                <DraggableField key={field.id} field={field} />
                                        ))}
                                    </div>
                                    )}
                                </div>
                            ))}
                                {/*!criteriaExpanded && (
                                    <div className='section-item show-more' onClick={() => setCriteriaExpanded(true)}>
                                        <span className='report-section-name'>Show more...</span>
                                    </div>
                                )*/}
                            </div>
                        </>
                    )}
                </div>
                
                <ColumnsList 
                    selectedFields={selectedFields}
                    onRemove={removeColumn}
                    moveColumn={moveColumn}
                    customFields={customFields}
                    onAddField={handleFieldDrop}
                    onEditBlankColumn={editBlankColumn}
                    onUpdateContactProperty={updateContactProperty}
                    loadingCustomFields={loadingCustomFields}
                />
            </div>
            {showBlankColumnModal && (
                <Modal
                    title="Add Blank Column"
                    instructions="Create a blank column with a custom header for manual data entry in Excel exports."
                    onClose={() => {
                        setShowBlankColumnModal(false);
                        setBlankColumnName('');
                    }}
                    footer={
                        <div className='modal-footer-actions'>
                            <button 
                                className='action alt' 
                                onClick={() => {
                                    setShowBlankColumnModal(false);
                                    setBlankColumnName('');
                                }}
                            >
                                Cancel
                            </button>
                            <button 
                                className='action' 
                                onClick={addBlankColumn}
                                disabled={!blankColumnName.trim()}
                            >
                                Add Column
                            </button>
                        </div>
                    }
                >
                    <div className='modal-content-wrapper'>
                        <div className='form-group'>
                            <label className='subtext'>Column Header<span className='required'>*</span></label>
                            <input
                                type='text'
                                className='form-control'
                                value={blankColumnName}
                                onChange={(e) => setBlankColumnName(e.target.value)}
                                placeholder='Enter column header name'
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' && blankColumnName.trim()) {
                                        addBlankColumn();
                                    }
                                }}
                            />
                        </div>
                    </div>
                </Modal>
            )}
        </DndProvider>
    );
};

export default ReportColumns;